import { JobFunction } from '@atlas/types'
import {
	Avatar,
	MenuContent,
	MenuHeader,
	MenuItem,
	MenuSeparator,
	MenuTrigger,
	MenuTriggerButton,
} from '@atlas/ui-primitives'
import { Power } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { jobFunctionLabel } from '@/lib/job-functions'
import { initials } from '@/lib/labels'

/**
 * The footer's own casing, NOT `lib/roles.roleLabel`: this line is rendered
 * `uppercase` by CSS to match the mockup, so it needs the raw words rather than
 * the directory's Title Case. Named distinctly so it does not read as a rival
 * implementation of the shared label.
 */
const footerCaption = (user: { jobFunction?: string; role?: string }): string => {
	// Prefer what the person DOES; fall back to what they may do, since a user with
	// no stated function still needs a caption.
	const stated =
		user.jobFunction && user.jobFunction !== JobFunction.UNASSIGNED
			? jobFunctionLabel(user.jobFunction)
			: (user.role ?? 'user')
	return stated.replace(/_/g, ' ').toLowerCase()
}

/**
 * The circle avatar from the mockup: a slightly-lifted disc with a hairline ring
 * and mono initials.
 *
 * The `Avatar` primitive with its `fallback`, restyled for the dark rail: the
 * primitive fills with `bg-ds-primary`, which on this surface would read as a
 * solid black disc rather than the lifted one the mockup shows. The geometry,
 * the fallback behaviour and the size scale still come from the primitive.
 */
function UserDisc({ name, className = 'h-8 w-8' }: { name: string; className?: string }) {
	return (
		<Avatar
			fallback={initials(name)}
			className={`shrink-0 border border-ds-background/12 bg-ds-background/8 font-mono text-ds-2xs text-ds-background/82 ${className}`}
		/>
	)
}

function SignOutButton() {
	const { logout } = useAuth()
	return (
		<button
			type="button"
			onClick={() => void logout()}
			aria-label="Sign out"
			title="Sign out"
			className="shrink-0 text-ds-background/35 hover:text-ds-background"
		>
			<Power className="h-4 w-4" strokeWidth={1.75} />
		</button>
	)
}

/**
 * The dark rail's user footer — expanded: disc + name + role beside the
 * sign-out button; rail: the disc and sign-out stacked. The dropdown keeps
 * our Settings entry; signing out is the mockup's explicit ⏻.
 *
 * `onMenuOpenChange` reports the dropdown's state to the rail, which must stay
 * expanded while the menu is open: the menu content is portaled OUTSIDE the
 * aside, so the pointer travelling to an item leaves the aside, and a rail that
 * collapsed there would swap this subtree out and unmount the menu mid-click.
 */
export function SidebarUserFooter({
	rail,
	onMenuOpenChange,
}: {
	rail: boolean
	onMenuOpenChange?: (open: boolean) => void
}) {
	const { user } = useAuth()
	const navigate = useNavigate()
	if (!user) return null
	const name = user.displayName || user.email || 'User'

	if (rail) {
		return (
			<div className="flex shrink-0 flex-col items-center gap-3 border-t border-ds-background/10 py-4">
				<UserDisc name={name} className="h-8.5 w-8.5" />
				<SignOutButton />
			</div>
		)
	}

	return (
		<div className="flex w-full shrink-0 items-center gap-2.75 border-t border-ds-background/10 px-4.5 py-4">
			<MenuTrigger onOpenChange={onMenuOpenChange}>
				<MenuTriggerButton className="flex min-w-0 flex-1 items-center gap-2.75 rounded-ds-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-ring">
					<UserDisc name={name} />
					<span className="min-w-0 flex-1 leading-tight">
						<span className="block truncate text-ds-xs font-medium text-ds-background">{name}</span>
						<span className="block truncate font-mono text-ds-4xs uppercase tracking-ds-wider text-ds-background/35">
							{footerCaption(user)} · TKF
						</span>
					</span>
				</MenuTriggerButton>
				<MenuContent className="w-56">
					<MenuHeader>{user.email}</MenuHeader>
					<MenuSeparator />
					<MenuItem onAction={() => void navigate('/settings')}>Settings</MenuItem>
				</MenuContent>
			</MenuTrigger>
			<SignOutButton />
		</div>
	)
}
